<template lang="pug">
b-modal(
  id='modal-heatmap-onboarding',
  :title='title',
  centered,
  size='lg',
  modal-class='heatmap-onboarding-modal',
  content-class='heatmap-onboarding-content',
  body-class='heatmap-onboarding-body',
  footer-class='heatmap-onboarding-footer',
  @hidden='onHidden'
)
  .heatmap-onboarding
    .heatmap-onboarding__viewport
      .heatmap-onboarding__track(:style='trackStyle')
        .heatmap-onboarding__slide(v-for='(slide, index) in slides', :key='index')
          .heatmap-onboarding__visual
            .heatmap-onboarding__image(v-if='slide.image')
              img(:src='slide.image', :alt='slide.heading')
            a.heatmap-onboarding__issue-pill(
              v-else-if='slide.issueHref',
              :href='slide.issueHref',
              target='_blank',
              rel='noopener noreferrer'
            )
              span.heatmap-onboarding__issue-icon !
              span ヒートマップが表示されない時
          .heatmap-onboarding__copy
            h4.heatmap-onboarding__heading {{ slide.heading }}
            p.heatmap-onboarding__body(v-html='slide.body')
    .heatmap-onboarding__dots
      span.heatmap-onboarding__dot(
        v-for='(slide, index) in slides',
        :key='index',
        :class="{ 'is-active': index === page }"
      )
  template(v-slot:modal-footer)
    button.btn.heatmap-onboarding__back(
      type='button',
      :disabled='isFirst',
      @click='prev'
    ) 戻る
    button.btn.heatmap-onboarding__next(
      type='button',
      @click='onPrimary'
    ) {{ primaryLabel }}
</template>

<script>
import UserApi from '../../apis/user'
import { HEATMAP_ONBOARDING_SLIDES, HEATMAP_ONBOARDING_TITLE } from '../../constants/heatmap_onboarding_slides'

export default {
  name: 'HeatmapOnboardingModal',
  props: {
    seen: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      page: 0,
      marked: false,
      title: HEATMAP_ONBOARDING_TITLE,
      slides: HEATMAP_ONBOARDING_SLIDES
    }
  },
  computed: {
    isFirst() {
      return this.page === 0
    },
    isLast() {
      return this.page === this.slides.length - 1
    },
    primaryLabel() {
      return this.isLast ? '使ってみる' : '次へ'
    },
    trackStyle() {
      return {
        transform: 'translateX(-' + (this.page * 100) + '%)'
      }
    }
  },
  methods: {
    open() {
      this.page = 0
      this.$bvModal.show('modal-heatmap-onboarding')
    },
    close() {
      this.$bvModal.hide('modal-heatmap-onboarding')
    },
    onPrimary() {
      if (this.isLast) {
        this.close()
      } else {
        this.next()
      }
    },
    next() {
      if (!this.isLast) {
        this.page += 1
      }
    },
    prev() {
      if (!this.isFirst) {
        this.page -= 1
      }
    },
    async onHidden() {
      if (this.marked) {
        return
      }
      this.marked = true
      try {
        await UserApi.markHeatmapOnboardingSeen()
      } catch (error) {
        this.marked = false
      }
    }
  },
  mounted() {
    if (!this.seen) {
      this.open()
    }
  }
}
</script>

<style lang="scss" scoped>
$green: #2fbf71;

.heatmap-onboarding {
  display: flex;
  flex-direction: column;
  height: 100%;
  text-align: center;
}

.heatmap-onboarding__viewport {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.heatmap-onboarding__track {
  display: flex;
  height: 100%;
  transition: transform 0.35s ease;
  will-change: transform;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
}

.heatmap-onboarding__slide {
  flex: 0 0 100%;
  width: 100%;
  height: 100%;
  padding: 8px 32px 4px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.heatmap-onboarding__visual {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 180px;
  height: 180px;
  margin-bottom: 28px;
}

.heatmap-onboarding__image {
  width: 100%;
  max-width: 600px;
  height: 100%;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    display: block;
    width: 100%;
    height: auto;
    max-height: 148px;
    object-fit: contain;
  }
}

.heatmap-onboarding__issue-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 16px 0 8px;
  border: 2px solid #e53935;
  border-radius: 999px;
  background: #fff;
  color: #e53935;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;

  &:hover,
  &:focus {
    color: #c62828;
    text-decoration: none;
  }
}

.heatmap-onboarding__issue-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #e53935;
  color: #fff;
  font-size: 14px;
}

.heatmap-onboarding__copy {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
}

.heatmap-onboarding__heading {
  flex: 0 0 auto;
  margin: 0 0 12px;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.5;
  color: #222;
}

.heatmap-onboarding__body {
  flex: 0 0 auto;
  margin: 0;
  overflow: visible;
  color: #555;
  font-size: 14px;
  line-height: 1.7;
  text-align: left;
}

.heatmap-onboarding__dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  flex: 0 0 auto;
  padding-top: 16px;
}

.heatmap-onboarding__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #d9d9d9;

  &.is-active {
    background: $green;
  }
}

.heatmap-onboarding__back,
.heatmap-onboarding__next {
  min-width: 96px;
  font-weight: 700;
}

.heatmap-onboarding__back {
  background: #f0f0f0;
  color: #666;
  border: none;

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
}

.heatmap-onboarding__next {
  background: $green;
  color: #fff;
  border: none;

  &:hover,
  &:focus {
    color: #fff;
    background: #26a861;
  }
}
</style>

<style lang="scss">
.heatmap-onboarding-modal {
  .modal-dialog {
    width: 800px;
    max-width: calc(100vw - 32px);
    margin: 1.75rem auto;
  }

  .heatmap-onboarding-content {
    width: 100%;
    height: 680px;
    max-height: calc(100vh - 40px);
    display: flex;
    flex-direction: column;
  }

  .heatmap-onboarding-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
  }
}

.heatmap-onboarding-footer {
  display: flex;
  justify-content: space-between;
  flex: 0 0 auto;
}
</style>
